import { useState, useEffect } from "react"
import { Command } from "cmdk"
import {
  Search, Users, Activity, MessageSquare, ClipboardList,
  Settings, Power, LayoutDashboard, Clock, FileText, Database,
  Plus, Megaphone, Swords, Tv, Keyboard,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/context/AuthContext"
import { useIsSupervisorOrAdmin } from "@/context/useRoles"

// Cross-component action bus — dispatch DOM custom events that pages listen for.
// This avoids mutable global refs and works across lazy-loaded pages.
export function dispatchPaletteAction(action: 'add-lead' | 'broadcast') {
  document.dispatchEvent(new CustomEvent(`palette:${action}`))
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const navigate        = useNavigate()
  const { signOut }     = useAuth()
  const isSupervisor    = useIsSupervisorOrAdmin()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // ⌘K or Ctrl+K — toggle command palette
      // NOTE: '?' key is owned exclusively by HotkeyHelp to prevent double-toggle
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(o => !o)
      }
    }
    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])


  function runCommand(command: () => void) {
    setOpen(false)
    // Slight delay so palette closes before action fires
    setTimeout(command, 80)
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Global Command Menu"
      className="cmdk-dialog transition-bg"
    >
      <div className="cmdk-overlay" onClick={() => setOpen(false)} />
      <div className="cmdk-content">
        <div className="cmdk-header">
          <Search size={16} className="cmdk-icon" />
          <Command.Input placeholder="Type a command or search…" />
        </div>
        <Command.List className="cmdk-list">
          <Command.Empty>No results found.</Command.Empty>

          {/* Navigation */}
          <Command.Group heading="Navigate">
            <Command.Item onSelect={() => runCommand(() => navigate("/dashboard"))}>
              <LayoutDashboard size={14} /> Dashboard
            </Command.Item>
            <Command.Item onSelect={() => runCommand(() => navigate("/leads"))}>
              <Database size={14} /> Lead Pipeline
            </Command.Item>
            <Command.Item onSelect={() => runCommand(() => navigate("/leaderboard"))}>
              <Activity size={14} /> Leaderboard
            </Command.Item>
            <Command.Item onSelect={() => runCommand(() => navigate("/sms"))}>
              <MessageSquare size={14} /> SMS Inbox
            </Command.Item>
            <Command.Item onSelect={() => runCommand(() => navigate("/scripts"))}>
              <FileText size={14} /> Script Library
            </Command.Item>
            <Command.Item onSelect={() => runCommand(() => navigate("/settings"))}>
              <Settings size={14} /> Settings
            </Command.Item>
          </Command.Group>

          {/* Actions — always available */}
          <Command.Group heading="Actions">
            <Command.Item onSelect={() => runCommand(() => dispatchPaletteAction('add-lead'))}>
              <Plus size={14} /> Add Lead <span style={{ marginLeft: 'auto', fontSize: '0.7rem', opacity: 0.5 }}>⌘N</span>
            </Command.Item>
          </Command.Group>

          {/* Supervisor tools */}
          {isSupervisor && (
            <Command.Group heading="Supervisor">
              <Command.Item onSelect={() => runCommand(() => navigate("/agents"))}>
                <Users size={14} /> Manage Agents
              </Command.Item>
              <Command.Item onSelect={() => runCommand(() => navigate("/reports"))}>
                <ClipboardList size={14} /> Reports &amp; Payroll
              </Command.Item>
              <Command.Item onSelect={() => runCommand(() => navigate("/shifts"))}>
                <Clock size={14} /> Shift Management
              </Command.Item>
              <Command.Item onSelect={() => runCommand(() => navigate("/war-room"))}>
                <Swords size={14} /> War Room
              </Command.Item>
              <Command.Item onSelect={() => runCommand(() => navigate("/board"))}>
                <Tv size={14} /> Live Board
              </Command.Item>
              <Command.Item onSelect={() => runCommand(() => dispatchPaletteAction('broadcast'))}>
                <Megaphone size={14} /> Broadcast to Floor <span style={{ marginLeft: 'auto', fontSize: '0.7rem', opacity: 0.5 }}>⌘⇧B</span>
              </Command.Item>
            </Command.Group>
          )}

          {/* Keyboard reference */}
          <Command.Group heading="Help">
            <Command.Item onSelect={() => {
              setOpen(false)
              // HotkeyHelp owns ? — fire it via its own custom event listener
              setTimeout(() => {
                document.dispatchEvent(new KeyboardEvent('keydown', { key: '?', bubbles: true }))
              }, 100)
            }}>
              <Keyboard size={14} /> View All Keyboard Shortcuts
            </Command.Item>
          </Command.Group>

          {/* Danger zone */}
          <Command.Group heading="Account">
            <Command.Item
              onSelect={() => runCommand(signOut)}
              className="cmdk-danger-item"
            >
              <Power size={14} /> Sign Out
            </Command.Item>
          </Command.Group>
        </Command.List>
      </div>
    </Command.Dialog>
  )
}
